import React, { useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useColorMode } from '@chakra-ui/react';
import './DarkModeToggle.css';

const DarkModeToggle: React.FC = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const [mounted, setMounted] = useState(false);
  const [localMode, setLocalMode] = useState(colorMode);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Update local state when colorMode changes
  useEffect(() => {
    setLocalMode(colorMode);
    console.log('DarkModeToggle: colorMode changed to:', colorMode);
  }, [colorMode]);

  const handleToggle = () => {
    console.log('Toggle clicked, current mode:', colorMode);
    
    // Toggle Chakra color mode
    toggleColorMode();
    
    // Also manually set the data-theme attribute
    const newMode = colorMode === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', newMode);
    document.body.setAttribute('data-theme', newMode);
    
    // Update local state immediately for instant UI feedback
    setLocalMode(newMode);
    
    console.log('Toggled to:', newMode);
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return <Box width="220px" height="40px" />;
  }

  const isDark = localMode === 'dark';

  return (
    <Box position="relative" display="inline-block">
      {/* Hidden checkbox for accessibility */}
      <input
        type="checkbox"
        id="darkModeToggle"
        checked={isDark}
        onChange={handleToggle}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
        aria-label="Toggle dark mode"
      />
      
      <Flex
        as="label"
        htmlFor="darkModeToggle"
        className="dark-mode-toggle"
        cursor="pointer"
        align="center"
        position="relative"
        width="220px"
        height="40px"
        borderRadius="25px"
        bg={isDark ? '#1a1a1a' : '#e5e5e5'}
        transition="all 0.3s ease"
        overflow="hidden"
        _hover={{ opacity: 0.85 }}
        userSelect="none"
      >
        {/* Sliding circle/pill */}
        <Box
        className="toggle-circle"
        position="absolute"
        width={isDark ? "50%" : "50%"}
        height="36px"
        bg={isDark ? '#2d2d2d' : 'white'}
        borderRadius="20px"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        left={isDark ? 'calc(50% - 2px)' : '2px'}
        top="2px"
        boxShadow="0 2px 10px rgba(0,0,0,0.1)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Icon in the circle */}
        {isDark ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
            <path d="M17 4a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" />
            <path d="M19 11h2m-1 -1v2" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="black">
            <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke="currentColor" strokeWidth="2"/>
          </svg>
        )}
      </Box>
      
      {/* Text labels */}
      <Flex width="100%" height="100%" position="relative" zIndex={0} pointerEvents="none">
        {/* Day Mode Text */}
        <Flex
          align="center"
          justify="center"
          flex={1}
          color={!isDark ? 'black' : '#666'}
          fontWeight={!isDark ? '700' : '400'}
          transition="all 0.3s ease"
        >
          <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.5px">
            Day Mode
          </Text>
        </Flex>

        {/* Night Mode Text */}
        <Flex
          align="center"
          justify="center"
          flex={1}
          color={isDark ? 'white' : '#666'}
          fontWeight={isDark ? '700' : '400'}
          transition="all 0.3s ease"
        >
          <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.5px">
            Night Mode
          </Text>
        </Flex>
      </Flex>
    </Flex>
    </Box>
  );
};

export default DarkModeToggle;